import { Keys } from "../functions/Keys"

type TupleFirst<T extends unknown[]> = T extends [infer F, ...unknown[]] ? F : never
type TupleRest<T extends unknown[]> = T extends [unknown, ...infer R] ? R : []

/**
 * Creates a type-safe dispatcher for discriminated union types.
 *
 * Routes items based on discriminant field (default: "enum") to specific handlers.
 * Supports partial handlers with fallback for unhandled cases.
 *
 * @param handlers - Partial map of discriminant values to handler functions
 * @param fallback - Handler for unhandled cases (throws by default)
 * @param discriminant - Field name to use for routing (default: "enum")
 * @returns Dispatcher function
 *
 * @example
 * ```ts
 * import { MethodDispatcher } from "@pdfking/tfunc"
 *
 * type Action = { enum: "add", value: number } | { enum: "reset" }
 * const dispatch = MethodDispatcher<[Action], void>({
 *   add: (action) => console.log(action.value),
 *   reset: () => console.log("reset")
 * })
 * dispatch({ enum: "add", value: 5 })
 * ```
 */
export const MethodDispatcher =
  <
    Args extends [Record<string, unknown>, ...unknown[]],
    Return,
    DiscriminantKey extends keyof TupleFirst<Args> = "enum",
    UnionType extends TupleFirst<Args> = TupleFirst<Args>,
    DiscriminantValues extends UnionType[DiscriminantKey] = UnionType[DiscriminantKey],
  >(
    handlers: Partial<{
      [V in DiscriminantValues & (number | string)]: (
        item: Extract<UnionType, Record<DiscriminantKey, V>>,
        ...restArgs: TupleRest<Args>
      ) => Return
    }>,
    // Method syntax for bivariance: a fallback typed on the remainder union —
    // every member the handlers do not name — is narrower than UnionType, and
    // as a strict function type it would be rejected for exactly the arms it
    // will never be called with.
    fallback: {
      bivariance(item: UnionType, ...restArgs: TupleRest<Args>): Return
    }["bivariance"] = (
      item,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ..._restArgs
    ) => {
      const allowedValues = Keys(handlers)
        .map(s => `${s}`)
        .join(", ")
      throw new Error(
        `Unknown ${String(discriminant)}: "${String(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item as any)[discriminant]
        )}". Expected one of: ${allowedValues}.`
      )
    },
    discriminant: DiscriminantKey = "enum" as DiscriminantKey
  ) =>
  (item: UnionType, ...restArgs: TupleRest<Args>): Return => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const discriminator = (item as any)[discriminant] as DiscriminantValues & (number | string)
    const handler = handlers[discriminator]
    if (handler) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return handler(item as any, ...restArgs)
    }
    return fallback(item, ...restArgs)
  }

/**
 * Creates a type-safe dispatcher requiring all cases to be handled.
 *
 * Like MethodDispatcher but requires exhaustive handlers for all discriminant values.
 * Ensures compile-time safety for complete union coverage.
 *
 * @param handlers - Complete map of all discriminant values to handlers
 * @returns Dispatcher function
 *
 * @example
 * ```ts
 * import { FullMethodDispatcher } from "@pdfking/tfunc"
 *
 * type Shape = { enum: "circle", radius: number } | { enum: "square", side: number }
 * const area = FullMethodDispatcher<[Shape], number>({
 *   circle: shape => Math.PI * shape.radius ** 2,
 *   square: shape => shape.side ** 2
 * })
 * area({ enum: "square", side: 3 }) // 9
 * ```
 */
export const FullMethodDispatcher = MethodDispatcher as <
  Args extends [Record<string, unknown>, ...unknown[]],
  Return,
  DiscriminantKey extends keyof TupleFirst<Args> = "enum",
  UnionType extends TupleFirst<Args> = TupleFirst<Args>,
  DiscriminantValues extends UnionType[DiscriminantKey] = UnionType[DiscriminantKey],
>(
  handlers: {
    [V in DiscriminantValues & (number | string)]: (
      item: Extract<UnionType, Record<DiscriminantKey, V>>,
      ...restArgs: TupleRest<Args>
    ) => Return
  },
  fallback?: (item: UnionType, ...restArgs: TupleRest<Args>) => Return
) => (item: UnionType, ...restArgs: TupleRest<Args>) => Return
